// src/components/ChatSidebar.tsx
import React, { useCallback, useMemo, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { InputBase, List, ListItem, ListItemButton, styled, InputAdornment, alpha } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TypingTitle from "./typingtitle";
import SearchIcon from '@mui/icons-material/Search';
import ChangePassword from "./ChangePassword";
import dayjs from "dayjs";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { deleteChat } from "../api/chat";
import { MessagesMap } from "../hooks/useChatMessages";
import {changePasswordRequest} from "../api/changePass";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Sidebar,
  Logo,
  ChatList,
  ChatListItem,
  ChatTitle,
  UserAvatarContainer,
  AvatarImage,
  DefaultAvatarIcon,
  SidebarHandle,
  NewChatButton
} from "./styled";


import { ChatSummary, DatabaseInfo } from "../types/chat";
import SettingsDrawer from "./Settings";
import SettingsModal from "./Settings";
import theme from "../theme/theme";

interface SidebarProps {
  username: string;
  fullName: string;
  avatar: string | null;
  navigate: ReturnType<typeof useNavigate>;
  email: string;
  savedChats: ChatSummary[];
  chatId: string;
  editingChatId: string | null;
  editTitle: string;
  editingChatHandlers: {
    setEditTitle: (v: string) => void;
    handleRename: (id: string) => void;
    setEditingChatId: (v: string | null) => void;
  };

  menuAnchorEl: HTMLElement | null;
  menuChatId: string | null;
  menuChatTitle: string;
  handleMenuOpen: (e: React.MouseEvent<HTMLElement>, c: ChatSummary) => void;
  handleMenuClose: () => void;
  handleMenuRename: () => void;
  handleMenuDelete: () => void;
  isCollapsed: boolean;
  toggleSidebar: () => void;
  
  availableDatabases: DatabaseInfo[];
  dbMenuAnchorEl: HTMLElement | null;
  handleDbMenuOpen: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  handleDbMenuClose: () => void;
  handleSelectDatabase: (db: DatabaseInfo) => void;

  settingsOpen: boolean;
  handleSelectChat: (id: string) => void;
  setSavedChats: React.Dispatch<React.SetStateAction<ChatSummary[]>>;
  setMessagesMap: React.Dispatch<React.SetStateAction<MessagesMap>>;
  avatarAnchorRef: React.RefObject<HTMLDivElement | null>;
  showAvatarSettings: boolean;
  setShowAvatarSettings: (v: boolean) => void;
  setSettingsOpen: (v: boolean) => void;
  onLogout: () => void;
}

const ChatSidebar: React.FC<SidebarProps> = ({
  email,
  username,
  fullName,
  avatar,
  isCollapsed,
  toggleSidebar,
  savedChats,
  chatId,
  editingChatId,
  editTitle,
  editingChatHandlers,
  menuAnchorEl,
  menuChatId,
  menuChatTitle,
  handleMenuOpen,
  handleMenuClose,
  handleMenuRename,
  handleMenuDelete,
  settingsOpen,
  availableDatabases,
  dbMenuAnchorEl,
  handleDbMenuOpen,
  handleDbMenuClose,
  handleSelectDatabase,
  handleSelectChat,
  avatarAnchorRef,
  showAvatarSettings,
  setShowAvatarSettings,
  setSettingsOpen,
  onLogout,
  setSavedChats,
  setMessagesMap,
  navigate,
}) => {
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

const handleOpenChangePw = () => setChangePwOpen(true);
const handleCloseChangePw = () => setChangePwOpen(false);
const splitTitle = (title: string) => {
  if (!title) return ["Nuova Chat", ""];
  const parts = title.split("—");
  const main = parts[0]?.trim() || "Nuova Chat";
  const db   = parts[1]?.trim() || "";
  return [main, db];
};
const filteredChats = useMemo(() => {
  const term = searchTerm.trim().toLowerCase();
  return term
    ? savedChats.filter(chat =>
        chat.title.toLowerCase().includes(term)
      )
    : savedChats;
}, [searchTerm, savedChats]);

  const truncate = useCallback(
    (t: string) => (t.length > 20 ? t.slice(0, 20) + "…" : t),
    []
  );
  const RenameInput = styled(InputBase)(({ theme }) => ({
    width: "100%",
    fontSize: "15px",
    fontWeight: 500,
    color: "#1c2a42",
    padding: "6px 12px",
    borderRadius: "12px",
    backgroundColor: "#f9fbff",
    border: "1px solid #c7d7f5",
    boxShadow: "inset 0 0 0 1px #dbe6ff",
    transition: "all 0.18s ease",
    "&:hover": {
      backgroundColor: "#f1f6ff",
      borderColor: "#a3c0f7",
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
    },
    "& input": {
      padding: 0,
    },
  }));
  

  
  return (
    <>
      <Sidebar collapsed={isCollapsed}>
        <Logo src="/logo.png" alt="Logo" />
        <ChatTitle>Benvenuto, {fullName}</ChatTitle>
        <Box
  sx={{
    width: "100%",
    mb: 2,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#edf4ff",
    px: 2,
  }}
>
  <TextField
    fullWidth
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter" && !searchTerm.trim()) {
        handleDbMenuOpen(e);
      }
    }}
    placeholder="Cerca o crea nuova conversazione"
    variant="outlined"
    sx={{
      maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
  borderRadius: "24px",
  backgroundColor: "#ffffff",
  paddingRight: "8px",
  paddingLeft: "8px",
  boxShadow: "none", 
  transition: "all 0.25s ease",
  outline: "none", 
  "&:hover": {
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)", 
    outline: "none",
  },
  "&.Mui-focused": {
    boxShadow: "0 0 0 2px #1c90ff33",
    outline: "none",
  },
  "& fieldset": {
    borderColor: "#dbe3f1",
  },
  "&:hover fieldset": {
    borderColor: "#c7d7f5",
  },
  "&.Mui-focused fieldset": {
    borderColor: "#1c90ff",
  },
},

    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ color: "#5a6b8c" }} />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title="Crea nuova chat">
            <IconButton
              edge="end"
              size="small"
              onClick={handleDbMenuOpen}
              sx={{
                bgcolor: "#f0f4ff",
                borderRadius: "12px",
                p: 0.8,
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "#d8e6ff",
                },
              }}
            >
              <AddIcon fontSize="small" sx={{ color: "#1c2a42" }} />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
    }}
  />
</Box>



  

        <ChatList>
        {filteredChats.map((chat) => {
                      const [shortTitle, dbName] = splitTitle(chat.title);
            const isEditing = editingChatId === chat.chatId;
  
            return (
              
              <ListItem disablePadding key={chat.chatId} sx={{ mb: 1 }}>
                
<ListItemButton
  selected={chat.chatId === chatId && !isEditing}
  onClick={() => handleSelectChat(chat.chatId)}
  sx={{
    borderRadius: '12px',
    border: chat.chatId === chatId ? '1px solid #1c90ff' : '1px solid transparent',
    transition: 'all 0.2s',
    py: 1.2,
  }}
>

                  <ListItemText
                    primary={
                      isEditing ? (
                        <RenameInput
                          value={editTitle}
                          onChange={e => editingChatHandlers.setEditTitle(e.target.value)}
                          onBlur={() => editingChatHandlers.handleRename(chat.chatId)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') editingChatHandlers.handleRename(chat.chatId);
                            if (e.key === 'Escape') editingChatHandlers.setEditingChatId(null);
                          }}
                          autoFocus
                          placeholder="New name…"
                        />
                      ) : (
                        <Tooltip title={chat.title} arrow>
                          <span
                            style={{
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 160,
                            }}
                          >
                            <TypingTitle title={shortTitle} />
                          </span>
                        </Tooltip>
                      )
                    }
                  />
                             <Tooltip title="Altre azioni" arrow>
                     <IconButton
                     size="small"
                     aria-label="Apri il menu della chat"
                     onClick={e => {
                       e.stopPropagation();
                       handleMenuOpen(e, chat);
                     }}
                     sx={{ ml: 1 }}
                     >
                     <MoreVertIcon fontSize="small" />
                     </IconButton>
                  </Tooltip>
                </ListItemButton>
              </ListItem>
            );
          })}
        </ChatList>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          elevation={4}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              minWidth: 156,
              boxShadow: 6,
              p: 0,
              bgcolor: "background.paper",
              "& .MuiMenuItem-root": {
                px: 2,
                py: 1,
                fontSize: 15.5,
                "&:hover": { bgcolor: "action.hover" },
              },
              "& .MuiDivider-root": { my: 0.4 },
              "& .MuiListItemIcon-root": { minWidth: 30 },
            },
          }}
          MenuListProps={{ dense: true }}
        >
          <MenuItem onClick={handleMenuRename}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Rename" />
          </MenuItem>
          <Divider sx={{ my: 0, borderColor: "#f3f3f3" }} />
          <MenuItem
            onClick={() => {
              const chatToDelete = savedChats.find((c) => c.chatId === menuChatId);
              if (!chatToDelete) return;
              if (!window.confirm("Eliminare la chat definitivamente?")) return;
  
              deleteChat(chatToDelete.id)
                .then(() => {
                  setSavedChats((chats) => chats.filter((c) => c.id !== chatToDelete.id));
                  setMessagesMap((prev) => {
                    const copy = { ...prev };
                    delete copy[chatToDelete.chatId];
                    return copy;
                  });
                  if (chatId === chatToDelete.chatId) {
                    const rest = savedChats.filter((c) => c.id !== chatToDelete.id);
                    navigate(rest.length ? `/chat/${rest[0].chatId}` : `/chat`, { replace: true });
                  }
                  handleMenuClose();
                })
                .catch((err) => alert("Ошибка при удалении: " + err.message));
            }}
            sx={{
              color: "error.main",
              fontWeight: 500,
              "& .MuiListItemIcon-root": { color: "error.main" },
              "&:hover": { bgcolor: "rgba(255,0,0,0.08)" }
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        </Menu>
  
        <Menu
          id="db-select-menu"
          anchorEl={dbMenuAnchorEl}
          open={Boolean(dbMenuAnchorEl)}
          onClose={handleDbMenuClose}
          elevation={2}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 0,
              minWidth: 160,
              boxShadow: "0px 4px 18px rgba(0,0,0,0.08)",
              bgcolor: "background.paper",
              "& .MuiMenuItem-root": {
                px: 1.5,
                py: 0.75,
                fontSize: 14.5,
                fontWeight: 500,
                color: "#1c2a42",
                "&:hover": {
                  bgcolor: "#f5f8ff",
                },
              },
            },
          }}
          MenuListProps={{ dense: true }}
        >
          {availableDatabases.map((db) => (
            <MenuItem
              key={db.id}
              onClick={() => {
                handleSelectDatabase(db);
                handleDbMenuClose();
              }}
            >
              {db.name}
            </MenuItem>
          ))}
        </Menu>
  
        <UserAvatarContainer>
          <Box
            ref={avatarAnchorRef as React.RefObject<HTMLDivElement>}
            onClick={() => setShowAvatarSettings(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              transition: "background 0.15s, box-shadow 0.18s",
              "&:hover": {
                background: "#f4f8fe",
                boxShadow: "0 2px 8px rgba(30,60,120,0.07)",
              },
              "&:active": {
                background: "#e9f2ff",
              }
            }}
          >
            {avatar ? (
              <AvatarImage src={avatar} />
            ) : (
              <DefaultAvatarIcon sx={{ fontSize: 34, color: "#657099" }} />
            )}
            <Typography
              variant="body2"
              sx={{
                ml: 1,
                fontSize: "16px",
                fontWeight: 500,
                color: "#23326a",
                fontFamily: "'Roboto', sans-serif",
                letterSpacing: 0.1
              }}
            >
              Mio profilo
            </Typography>
          </Box>
  
          <Menu
            anchorEl={avatarAnchorRef.current}
            open={showAvatarSettings}
            onClose={() => setShowAvatarSettings(false)}
            elevation={4}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "right" }}
            PaperProps={{
              sx: {
                borderRadius: 2.5,
                minWidth: 156,
                boxShadow: 6,
                p: 0,
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1,
                  fontSize: 15.5,
                  "&:hover": { bgcolor: "action.hover" },
                },
                "& .MuiDivider-root": { my: 0.4 },
                "& .MuiListItemIcon-root": { minWidth: 30 },
              },
            }}
            MenuListProps={{ dense: true }}
          >
            <Box sx={{ px: 2, py: 1, fontWeight: 600, color: "text.primary", fontSize: 15.5 }}>
              {(fullName || '').split(' ')[0]}
            </Box>
  
            <Divider sx={{ my: 0, borderColor: "#f3f3f3" }} />
  
            <MenuItem
              onClick={() => {
                setSettingsOpen(true);
                setShowAvatarSettings(false);
              }}
            >
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>
  
            <MenuItem
              onClick={onLogout}
              sx={{
                color: "error.main",
                "& .MuiListItemIcon-root": { color: "error.main" },
                fontWeight: 500,
              }}
            >
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Log out" />
            </MenuItem>
          </Menu>
        </UserAvatarContainer>
  
        <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          fullName={fullName}
          username={username}
          email={email}
          onLogout={onLogout}
          onOpenChangePw={handleOpenChangePw}
        />
      </Sidebar>
  
      <Dialog
        open={changePwOpen}
        onClose={handleCloseChangePw}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 6px 32px rgba(25, 118, 210, 0.12)",
            background: "#f7f9fc",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 22,
            px: 3,
            py: 2.2,
            borderBottom: "1px solid #e3e8f0",
            color: "#1c2a42",
          }}
        >
          Cambia password
          <IconButton
            aria-label="close"
            onClick={handleCloseChangePw}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
  
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.2,
              bgcolor: "#fff",
              p: 3,
              borderRadius: 3,
              boxShadow: "0px 1px 3px rgba(0,0,0,0.04)"
            }}
          >
            <ChangePassword onSuccess={handleCloseChangePw} />
          </Box>
        </DialogContent>
      </Dialog>
    
    </>
  );
};

export default ChatSidebar;
